import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { API, useAuth } from "@/App";

const MilestoneTimelineCard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/wellness/pregnancy/milestones`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading || !data) return null;

  const pct = Math.round((data.achieved_count / data.total_count) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      data-testid="milestone-timeline-card"
    >
      <Card className="glass border-pink-400/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-300" />
              Pregnancy Milestones
            </CardTitle>
            <span className="text-sm text-mystic-200" data-testid="milestone-progress-text">
              {data.achieved_count}/{data.total_count}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden mb-5">
            <div
              className="h-full bg-gradient-to-r from-pink-400 to-amber-300 rounded-full transition-all"
              style={{ width: `${pct}%` }}
              data-testid="milestone-progress-bar"
            />
          </div>

          <ol className="relative border-l border-white/10 pl-5 space-y-3 max-h-72 overflow-y-auto">
            {data.milestones.map((m) => {
              const isNext = data.next_milestone && data.next_milestone.week === m.week;
              return (
                <li key={m.week} className="relative" data-testid={`milestone-week-${m.week}`}>
                  <span
                    className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full ring-4 ${
                      m.achieved
                        ? "bg-amber-300 ring-amber-300/20"
                        : isNext
                        ? "bg-pink-400 ring-pink-400/30 animate-pulse"
                        : "bg-white/20 ring-white/5"
                    }`}
                  />
                  <div
                    className={`flex items-center gap-2 ${
                      m.achieved ? "text-white" : isNext ? "text-pink-100" : "text-mystic-200/60"
                    }`}
                  >
                    <span className="text-lg" aria-hidden>{m.emoji}</span>
                    <span className="font-medium text-sm">{m.title}</span>
                    {isNext && (
                      <span className="ml-auto text-xs flex items-center gap-1 text-pink-300">
                        <Sparkles className="w-3 h-3" /> next
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-mystic-300/60 ml-7">Week {m.week}</span>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MilestoneTimelineCard;
